/**
 *
 *  Organizations/Sequences
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
// import PropTypes from 'prop-types'
import styled from '@emotion/styled'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

// Components
import SequenceForm from './Form'
import Modal from 'components/Modal'
import { ExpandableCard } from '../Settings'

// Utils
import { COLORS } from 'global-styles'

const SequenceBox = styled.div`
	padding: 10px 15px;
	display: flex;
	justify-content: space-between;
	align-items: center;

	&:hover {
		background-color: ${COLORS.lightGrey};
	}

	.btn {
		padding: 1px 5px;
	}

	.outer-circle {
		width: 17px;
		height: 17px;
		border: 1px solid ${COLORS.grey_c};
		border-radius: 50%;
		margin-right: 7px;
		margin-top: -2px;
		line-height: 20px;
		display: flex;
		align-items: center;

		.inner-circle {
			width: 8px;
			height: 8px;
			border-radius: 50%;
			background-color: ${COLORS.primary};
			margin-left: auto;
			margin-right: auto;
		}
	}
`

const GET_ORGANIZATION_SEQUENCES = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			sequences_defaults
			sequences {
				id
				name
				ready_made_topic {
					id
					value
					text
				}
			}
		}
	}
`

const UPDATE_ORGANIZATION = gql`
	mutation updateOrganization($id: ID!, $sequences_defaults: JSON) {
		updateOrganization(input: { id: $id, sequences_defaults: $sequences_defaults }) {
			organization {
				id
				name
			}
		}
	}
`

const DELETE_SEQUENCE = gql`
	mutation deleteSequence($id: ID!) {
		deleteSequence(input: { id: $id }) {
			destroyed
		}
	}
`

export const ready_made_topics = {
	social_security_567: 'Social Security 567',
	taxes_in_retirement_567: 'Taxes in Retirement 567',
	estate_planning_567: 'Estate Planning 567',
	enlighten_567: 'Enlighten 567',
	'retirement_helper___tax_(canada)_567': 'Retirement Helper (Canada) 567',
}

function TopicSequences({ sequences = [], selected_sequence = null, topic, onSelect, onDelete, onCopy, loading }) {
	if (!sequences.length) return <div className="border p-2 mt-3 text-center">No Sequences for This Topic</div>

	return (
		<div className="mt-3 border border-bottom-0">
			{sequences.map((sequence) => (
				<SequenceBox key={sequence.id} className="border-bottom">
					<div className="d-flex align-items-center">
						{/** <div className="outer-circle">{is_selected && <div className="inner-circle"></div>}</div>* */}
						<span>{sequence.name}</span>
					</div>

					<div>
						<button
							className="btn btn-primary btn-sm ml-1"
							onClick={(e) => {
								e.stopPropagation()
								onCopy(sequence)
							}}
							disabled={loading}
						>
							Copy
						</button>

						<Link
							className="btn btn-warning btn-sm ml-1"
							to={`/sequences/${sequence.id}`}
							onClick={(e) => e.stopPropagation()}
							disabled={loading}
						>
							Edit
						</Link>

						<button
							className="btn btn-danger btn-sm ml-1"
							onClick={(e) => {
								e.stopPropagation()
								onDelete(sequence)
							}}
							disabled={loading}
						>
							Delete
						</button>
					</div>
				</SequenceBox>
			))}
		</div>
	)
}

function Sequences({ organization_id }) {
	const [open_form, setOpenForm] = useState(null)

	const {
		data: { organization },
		loading: fetching,
	} = useQuery(GET_ORGANIZATION_SEQUENCES, {
		variables: { id: organization_id },
		channels: [organization_id],
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const [updateOrganization, { loading: updatingOrganization }] = useMutation(UPDATE_ORGANIZATION)

	const [deleteSequence, { loading: deletingOrganization }] = useMutation(DELETE_SEQUENCE)

	if (!organization) return null

	const handleSequencesUpdate = (topic, new_id) => {
		const new_sequences_defaults = { ...organization.sequences_defaults }

		new_sequences_defaults[topic] = new_id

		updateOrganization({ variables: { id: organization_id, sequences_defaults: new_sequences_defaults } })
	}

	const handleDeleteSequence = (sequence) => {
		swal({
			title: 'Are you sure you want to delete this Sequence?',
			text: `Sequence - ${sequence.name} - will be Deleted.`,
			type: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, Delete this Sequence!',
			cancelButtonText: 'Cancel',
			confirmButtonClass: 'btn btn-danger d-inline',
			cancelButtonClass: 'btn btn-default d-inline',
			buttonsStyling: false,
		}).then(
			() => deleteSequence({ variables: { id: sequence.id } }),
			() => {},
		)
	}

	const loading = updatingOrganization || deletingOrganization || fetching

	return (
		<div>
			<div className="container-fluid px-0">
				<button className="btn btn-primary" onClick={() => setOpenForm({})}>
					<FontAwesomeIcon icon="plus" style={{ marginBottom: '-1px', marginRight: '3px' }} /> Create New
					Sequence
				</button>

				<div className="row mt-3">
					{Object.keys(ready_made_topics).map((topic) => (
						<div className="col-md-6">
							<ExpandableCard title={`${ready_made_topics[topic]} Sequences`}>
								<TopicSequences
									selected_sequence={organization.sequences_defaults[topic]}
									sequences={organization.sequences?.filter(
										(sequence) => sequence.ready_made_topic.value === topic,
									)}
									topic={topic}
									onSelect={handleSequencesUpdate}
									onDelete={handleDeleteSequence}
									onCopy={(sequence) =>
										setOpenForm({
											id: sequence.id,
											name: `${sequence.name} (Copy)`,
											topic_value: topic,
											is_copy: true,
										})
									}
									loading={loading}
								/>
							</ExpandableCard>
						</div>
					))}
				</div>
			</div>

			{!!open_form && (
				<Modal show modalDidClose={() => setOpenForm(null)} style={{ width: '450px', padding: '25px' }}>
					<SequenceForm
						sequence={open_form}
						handleClose={() => setOpenForm(null)}
						organization_id={organization_id}
					/>
				</Modal>
			)}
		</div>
	)
}

Sequences.propTypes = {}

export default Sequences
